import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApolloClient, useQuery } from '@apollo/react-hooks'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { formatDateTimeLocal } from '@/utils/timezone'
import { PHOTO, PHOTOS } from '../api'
import { moveIds } from '../lib/albums'
import { buildGridRows, columnsFor, groupAsOne, groupByDay, mergePhotoPages, processingIds, rangeSelection, tileSize } from '../lib/timeline'
import type { Photo, PhotoFilterInput, PhotoPage } from '../types'
import PhotoTile from './PhotoTile'
import PhotoTimelineSkeleton from './PhotoTimelineSkeleton'

const GAP = 8
const HEADER_H = 40
const PAGE_SIZE = 100

interface Props {
  projectId: string
  filter: PhotoFilterInput
  size: 'S' | 'M' | 'L'
  selectedIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
  onOpen: (photoId: string, orderedIds: string[]) => void
  onPhotosChange?: (photos: Photo[]) => void
  /** Server-side total for the current filter (not just the loaded pages). */
  onTotalChange?: (total: number | null) => void
  /** Album view with edit rights: receives the full new id order after a tile-to-tile drag. */
  onReorder?: (orderedIds: string[]) => void
  onPhotoContextMenu?: (photoId: string, e: React.MouseEvent) => void
  className?: string
}

const PhotoTimeline: React.FC<Props> = ({ projectId, filter, size, selectedIds, onSelectionChange, onOpen, onPhotosChange, onTotalChange, onReorder, onPhotoContextMenu, className }) => {
  const { t, i18n } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<string | null>(null)
  const loadingMoreRef = useRef(false)
  const [width, setWidth] = useState(0)

  const { data, loading, error, fetchMore } = useQuery<{ photos: PhotoPage }>(PHOTOS, {
    variables: { projectId, filter, first: PAGE_SIZE },
    notifyOnNetworkStatusChange: true,
  })

  const photos: Photo[] = data?.photos?.nodes ?? []
  const nextCursor = data?.photos?.nextCursor ?? null
  useEffect(() => {
    onPhotosChange?.(photos)
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps
  const totalCount = data?.photos?.totalCount
  // null while a query is in flight (stale count) and once the timeline unmounts.
  useEffect(() => {
    onTotalChange?.(loading || typeof totalCount !== 'number' ? null : totalCount)
  }, [totalCount, loading]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onTotalChange?.(null), []) // eslint-disable-line react-hooks/exhaustive-deps
  const anyProcessing = photos.some((p) => p.status === 'PROCESSING')

  // Refresh only the PROCESSING photos by id (they normalise to Photo:<id> and update in place);
  // re-running the list query would drop fetchMore pages and re-sign every thumbnail URL.
  const client = useApolloClient()
  const photosRef = useRef(photos)
  photosRef.current = photos
  useEffect(() => {
    if (!anyProcessing) return undefined
    const timer = setInterval(() => {
      processingIds(photosRef.current).forEach((id) => {
        client.query({ query: PHOTO, variables: { id }, fetchPolicy: 'network-only' }).catch(() => undefined)
      })
    }, 5000)
    return () => clearInterval(timer)
  }, [anyProcessing, projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined
    setWidth(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => setWidth(el.clientWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [loading, photos.length === 0]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = columnsFor(width, size)
  const inAlbum = !!filter.albumId
  const groups = useMemo(() => (inAlbum ? groupAsOne(photos) : groupByDay(photos)), [data, inAlbum]) // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => buildGridRows(groups, columns).filter((r) => !inAlbum || r.kind !== 'header'), [groups, columns, inAlbum])
  const orderedIds = useMemo(() => groups.reduce<string[]>((acc, g) => acc.concat(g.photos.map((p) => p.id)), []), [groups])
  const tileH = columns > 0 && width > 0 ? (width - (columns - 1) * GAP) / columns : tileSize(size)

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: (i) => (rows[i].kind === 'header' ? HEADER_H : tileH + GAP),
    overscan: 4,
  })
  useEffect(() => {
    virtualizer.measure()
  }, [tileH, rows]) // eslint-disable-line react-hooks/exhaustive-deps

  const items = virtualizer.getVirtualItems()
  const lastIndex = items.length > 0 ? items[items.length - 1].index : -1

  useEffect(() => {
    if (!nextCursor || loadingMoreRef.current || lastIndex < rows.length - 3) return
    loadingMoreRef.current = true
    fetchMore({
      variables: { after: nextCursor },
      updateQuery: (prev: { photos: PhotoPage }, { fetchMoreResult }: { fetchMoreResult?: { photos: PhotoPage } }) => {
        if (!fetchMoreResult) return prev
        return {
          ...prev,
          photos: {
            ...fetchMoreResult.photos,
            nodes: mergePhotoPages(prev.photos.nodes, fetchMoreResult.photos.nodes),
          },
        }
      },
    }).then(
      () => { loadingMoreRef.current = false },
      () => { loadingMoreRef.current = false },
    )
  }, [lastIndex, nextCursor, rows.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback((photoId: string, e: React.MouseEvent) => {
    const next = new Set(selectedIds)
    if (e.shiftKey && anchorRef.current) {
      rangeSelection(orderedIds, anchorRef.current, photoId).forEach((id) => next.add(id))
    } else {
      if (next.has(photoId)) next.delete(photoId)
      else next.add(photoId)
      anchorRef.current = photoId
    }
    onSelectionChange(next)
  }, [selectedIds, orderedIds, onSelectionChange])

  const orderedIdsRef = useRef(orderedIds)
  orderedIdsRef.current = orderedIds
  const handleReorderDrop = useCallback((draggedIds: string[], targetId: string, before: boolean) => {
    const next = moveIds(orderedIdsRef.current, draggedIds, targetId, before)
    if (next.join() !== orderedIdsRef.current.join()) onReorder?.(next)
  }, [onReorder])

  const toggleDay = (dayKey: string) => {
    const ids = (groups.find((g) => g.dayKey === dayKey)?.photos ?? []).map((p) => p.id)
    const next = new Set(selectedIds)
    const all = ids.length > 0 && ids.every((id) => next.has(id))
    ids.forEach((id) => (all ? next.delete(id) : next.add(id)))
    onSelectionChange(next)
  }

  if (loading && photos.length === 0) return <PhotoTimelineSkeleton />
  if (error && photos.length === 0) return <div className="p-8 text-center text-destructive">{t('Photos_load_error')}</div>
  if (photos.length === 0) return <div className="p-8 text-center text-muted-foreground">{t('Photos_empty')}</div>

  const selectionActive = selectedIds.size > 0
  const dragSelection = Array.from(selectedIds)

  return (
    <div ref={scrollerRef} className={cn('h-[calc(100vh-14rem)] min-h-[320px] overflow-y-auto', className)}>
      <div
        className="relative w-full"
        // ui-drift: dynamic
        style={{ height: virtualizer.getTotalSize() }}
      >
        {items.map((item) => {
          const row = rows[item.index]
          if (!row) return null
          if (row.kind === 'header') {
            const dayIds = (groups.find((g) => g.dayKey === row.dayKey)?.photos ?? []).map((p) => p.id)
            const selectedCount = dayIds.filter((id) => selectedIds.has(id)).length
            return (
              <div
                key={`h-${row.dayKey}`}
                className="absolute left-0 top-0 flex w-full items-center gap-2"
                // ui-drift: dynamic
                style={{ height: HEADER_H, transform: `translateY(${item.start}px)` }}
              >
                <Checkbox
                  checked={selectedCount === 0 ? false : selectedCount === dayIds.length ? true : 'indeterminate'}
                  aria-label={t('Photos_select_day')}
                  onClick={() => toggleDay(row.dayKey)}
                />
                <span className="font-semibold">
                  {formatDateTimeLocal(row.date, i18n.language, 'PPPP')}
                </span>
                <span className="text-sm text-muted-foreground">{t('Photos_day_count', { count: row.count })}</span>
              </div>
            )
          }
          return (
            <div
              key={`r-${row.dayKey}-${row.photos[0].id}`}
              className="absolute left-0 top-0 grid w-full gap-2"
              // ui-drift: dynamic
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, height: tileH, transform: `translateY(${item.start}px)` }}
            >
              {row.photos.map((photo) => {
                const selected = selectedIds.has(photo.id)
                return (
                  <PhotoTile
                    key={photo.id}
                    photo={photo}
                    size={size}
                    selected={selected}
                    selectionActive={selectionActive}
                    dragIds={selected ? dragSelection : [photo.id]}
                    onToggle={(e) => toggle(photo.id, e)}
                    onOpen={() => onOpen(photo.id, orderedIds)}
                    onReorderDrop={onReorder ? handleReorderDrop : undefined}
                    onContextMenu={onPhotoContextMenu ? (e) => onPhotoContextMenu(photo.id, e) : undefined}
                  />
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PhotoTimeline
